import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import {
  createFareMarker,
  createNoFareMarker,
  createOriginMarker,
  DestinationMarkerData,
  isFareMarker,
  MarkerData
} from '../classes/MarkerData';
import useFaresApi from '../filters/useFares';
import useFilters from '../filters/useFilters';
import { Airport } from '../filters/useLocations';
import { IMCFare } from '../types';
import { MapActions } from '../VectorMap';
import useIMCMarkersMap from './useIMCMarkersMap';
import create from 'zustand';
import { combine } from 'zustand/middleware';

export type Status =
  | 'SKELETON'
  | 'LOADED'
  | 'LOADING_ORIGINS'
  | 'LOADING_DESTINATIONS'
  | 'LOADING_VISIBLE_FARES';

export const useStore = create(
  combine(
    {
      status: 'SKELETON' as Status,
      visibleDestinations: [] as string[] | null,
      preDestinations: [] as $TSFixMe[],
      userInteraction: false,
      isFullScreen: (undefined as unknown) as Boolean,
      showTopOffers: true,
      showAlertInfo: true,
      zoom: 1,
      showNoResult: false,
      selectedOrigin: undefined,
      changedOrigin: undefined
    },
    (set) => ({
      setStatus: (status: Status) => set({ status }),
      setChangedOrigin: (changedOrigin: any) => set({ changedOrigin }),
      setSelectedOrigin: (selectedOrigin: any) => set({ selectedOrigin }),
      setVisibleDestinations: (visibleDestinations: string[] | null) =>
        set({ visibleDestinations }),
      setPreDestinations: (preDestinations: $TSFixMe[]) => set({ preDestinations }),
      startUserInteraction: () => set({ userInteraction: true }),
      hideAlertInfo: () => set({ showAlertInfo: false }),
      stopUserInteraction: () => set({ userInteraction: false }),
      toggleFullScreen: (isFullScreen: boolean) => set({ isFullScreen }),
      toggleTopOffers: (showTopOffers: boolean) => set({ showTopOffers }),
      setShowNoResult: (showNoResult: boolean) => set({ showNoResult }),
      setZoom: (zoom: number) => set({ zoom })
    })
  )
);

type State = ReturnType<typeof useStore['getState']>;
export const getShowNoResult = (state: State) => state.showNoResult;
export const getChangedOrigin = (state: State) => state.changedOrigin;
export const getSelectedOrigin = (state: State) => state.selectedOrigin;
export const setShowNoResult = (state: State) => state.setShowNoResult;
export const setSelectedOrigin = (state: State) => state.setSelectedOrigin;
export const setChangedOrigin = (state: State) => state.setChangedOrigin;
export const visibleDestinationsSelector = (state: State) => state.visibleDestinations;
export const setVisibleDestinationsSelector = (state: State) => state.setVisibleDestinations;
export const getPreDestinationsSelector = (state: State) => state.preDestinations;
export const setPreDestinationsSelector = (state: State) => state.setPreDestinations;
export const userInteractionSelector = (state: State) => state.userInteraction;
export const startUserInteractionSelector = (state: State) => state.startUserInteraction;
export const stopUserInteractionSelector = (state: State) => state.stopUserInteraction;
export const isFullScreenSelector = (state: State) => state.isFullScreen;
export const toggleFullScreenSelector = (state: State) => state.toggleFullScreen;
export const isShowTopOffersSelector = (state: State) => state.showTopOffers;
export const toggleTopOffersSelector = (state: State) => state.toggleTopOffers;
export const getZoomSelector = (state: State) => state.zoom;
export const setZoomSelector = (state: State) => state.setZoom;
export const isAlertShowSelector = (state: State) => state.showAlertInfo;
export const hideAlertInfoSelector = (state: State) => state.hideAlertInfo;

const useOriginMarkers = (filters: ReturnType<typeof useFilters>) => {
  const [selectedValue, setSelectedValue] = useState(filters.locations.origin.value || '');
  const markers = useMemo(() => {
    const data = filters.locations.origin.data;
    return data ? data.map(createOriginMarker) : [];
  }, [filters.locations.origin.data]);

  const { has, get } = useIMCMarkersMap(markers);
  const selectedIndex = useMemo(() => {
    return has(selectedValue) ? get(selectedValue) : -1;
  }, [get, has, selectedValue]);
  const isOriginSelected = selectedIndex !== -1;
  const selectedOriginMarker = markers[selectedIndex] || null;

  useEffect(() => {
    setSelectedValue(filters.locations.origin.value);
  }, [filters.locations.origin.value]);

  return {
    markers,
    selectedOriginMarker,
    isOriginSelected,
    setSelectedOriginMarker: setSelectedValue,
    getOrigin: get,
    hasOrigin: has
  };
};

interface DestinationState {
  loadingIndex: number;
  selectedIndex: number;
  destinationMarkers: DestinationMarkerData[];
}

const INITIAL_STATE = {
  selectedIndex: -1,
  loadingIndex: -1,
  destinationMarkers: []
};

const useDestinationMarkers = (params: {
  selectedOrigin: MarkerData;
  isOriginSelected: boolean;
  destinations?: Airport[];
  fares: ReturnType<typeof useFaresApi>;
}) => {
  const { selectedOrigin, isOriginSelected, destinations, fares } = params;
  const [state, setState] = useState<DestinationState>(INITIAL_STATE);
  const [isClearDestination, setIsClearDestinationState] = useState<boolean>(true);
  const { destinationMarkers, selectedIndex, loadingIndex } = state;

  const { has, get } = useIMCMarkersMap(destinations);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, [setState]);

  useEffect(() => {
    if (!isOriginSelected) {
      reset();
    }
  }, [isOriginSelected, reset, setState]);

  useEffect(() => {
    reset();
    if (destinations && selectedOrigin) {
      const markers: DestinationMarkerData[] = destinations.map((marker) =>
        createNoFareMarker(marker, selectedOrigin)
      );
      if (fares.data) {
        let i = 0;
        for (let fare of fares.data) {
          if (has(fare.destination)) {
            const index = get(fare.destination);
            const marker = createFareMarker(markers[index], fare);
            //INFO: The map was conceptualized to represent one-many and not many-many.
            //      The many-many problem arises when an airport is selected that groups
            //      several airports internally.
            if (!Boolean(markers[index].fare)) {
              marker.order = i++;
              markers[index] = marker;
            }
          }
        }
      }
      setState((prevState) => {
        return {
          ...prevState,
          destinationMarkers: markers
        };
      });
    }
  }, [destinations, reset, selectedOrigin, setState, fares.data, has, get]);

  const isDestinationSelected = selectedIndex !== -1;
  useEffect(() => {
    if (isDestinationSelected) {
      setIsClearDestinationState(false);
    }
  }, [isDestinationSelected]);
  const selectedDestinationMarker = destinationMarkers[selectedIndex];
  const isLoadingDestination = loadingIndex !== -1;

  const loadingMarker = useMemo(() => destinationMarkers[loadingIndex] || null, [
    loadingIndex,
    destinationMarkers
  ]);

  const currentFares = useMemo(() => {
    return destinationMarkers
      .filter(isFareMarker)
      .sort(({ order: a }, { order: b }) => a - b)
      .map(({ fare }) => fare);
  }, [destinationMarkers]);

  const setSelectedMarker = useCallback(
    (index: number) => {
      const marker = destinationMarkers[index];
      setState((prevState) => {
        if (marker.markerType === 'noFare') {
          const newMarkers = [...prevState.destinationMarkers];
          newMarkers[index] = createNoFareMarker(marker, selectedOrigin);
          return {
            ...prevState,
            selectedIndex: index,
            destinationMarkers: newMarkers
          };
        }
        return {
          ...prevState,
          selectedIndex: index
        };
      });
    },
    [destinationMarkers, selectedOrigin]
  );

  const setDestinationFare = useCallback(
    (index: number, data: IMCFare | null) => {
      setState((prevState) => {
        const marker = destinationMarkers[index];
        const newMarkers = [...prevState.destinationMarkers];
        newMarkers[index] = data
          ? createFareMarker(marker, data)
          : createNoFareMarker(marker, selectedOrigin);
        return {
          ...prevState,
          destinationMarkers: newMarkers
        };
      });
    },
    [destinationMarkers, selectedOrigin]
  );

  const setSelectedDestinationMarker = useCallback(
    async (id: string) => {
      return new Promise((resolve) => {
        if (has(id)) {
          const index = get(id);
          const marker = destinationMarkers[index];
          setSelectedMarker(index);
          if (marker.markerType === 'noFare') {
            if (destinations && Array.isArray(destinations) && destinations.length > 0) {
              const marker = destinations[0];
              setDestinationFare(index, isFareMarker(marker) ? marker.fare : null);
            } else {
              setDestinationFare(index, null);
            }
            resolve(undefined);
          }
        }
      });
    },
    [destinationMarkers, destinations, get, has, setDestinationFare, setSelectedMarker]
  );

  return {
    destinationMarkers,
    selectedDestinationMarker,
    loadingMarker,
    isDestinationSelected,
    isLoadingDestination,
    setSelectedDestinationMarker,
    currentFares,
    setIsClearDestination: setIsClearDestinationState,
    isClearDestination
  };
};

const useInteractiveMap = (params: {
  filters: ReturnType<typeof useFilters>;
  fares: ReturnType<typeof useFaresApi>;
  mapRef: RefObject<MapActions>;
  showInitialData?: boolean;
  centerOriginLogic?: boolean;
}) => {
  const { filters, fares, mapRef, centerOriginLogic } = params;

  const {
    markers: originMarkers,
    selectedOriginMarker,
    setSelectedOriginMarker,
    isOriginSelected,
    hasOrigin,
    getOrigin
  } = useOriginMarkers(filters);

  const {
    destinationMarkers,
    selectedDestinationMarker,
    isDestinationSelected,
    isLoadingDestination,
    setSelectedDestinationMarker,
    currentFares,
    setIsClearDestination,
    isClearDestination
  } = useDestinationMarkers({
    selectedOrigin: selectedOriginMarker,
    isOriginSelected,
    destinations: filters.locations.destination.data,
    fares
  });

  const [filtersHasChange, setFiltersHasChange] = useState(false);

  const clearMapVisibleMarkers = useCallback(() => {
    mapRef.current?.clearVisibleMarkers();
  }, [mapRef]);

  useEffect(() => {
    if (mapRef.current) {
      setFiltersHasChange(false);
      mapRef.current.updateSize();
    }
  }, [filters.locations.origin.value, mapRef]);

  const selectOrigin = useCallback(
    (marker: MarkerData) => {
      clearMapVisibleMarkers();
      setSelectedOriginMarker(marker.value);
    },
    [clearMapVisibleMarkers, setSelectedOriginMarker]
  );

  useEffect(() => {
    const fun = async () => {
      const id = filters.locations.origin.value;
      if (id && hasOrigin(id)) {
        let index = getOrigin(id);
        const marker = originMarkers[index];
        if (centerOriginLogic) {
          mapRef.current?.moveTo(marker.location);
        }
        selectOrigin(marker);
      }
    };
    fun();
    // eslint-disable-next-line
  }, [filters.locations.origin.value]);

  useEffect(() => {
    if (isOriginSelected) {
      selectOrigin(selectedOriginMarker);
      if (centerOriginLogic) {
        mapRef.current?.setView(selectedOriginMarker.location);
      }
    } else {
      setFiltersHasChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOriginSelected, mapRef, selectedOriginMarker]);

  const dispatch = filters.dispatch;

  const setSelectedMarker = useCallback(
    (marker: MarkerData, sendTrackingEvent: boolean = true, makeMoveAction: boolean = true) => {
      if (!isOriginSelected) {
        filters.locations.origin.onChange(marker);
      } else {
        if (makeMoveAction && centerOriginLogic) {
          mapRef.current?.moveTo(marker.location);
        }
        setSelectedDestinationMarker(marker.value);
        if (sendTrackingEvent) {
          dispatch({
            type: 'SELECT_MAP_DESTINATION',
            payload: {
              value: marker.value,
              start: marker.fare?.departureDate,
              end: marker.fare?.returnDate
            }
          });
        }
      }
    },
    // FIXME: Check why dispatch doesn't have an stable identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.locations.origin, isOriginSelected, mapRef, setSelectedDestinationMarker]
  );

  const route = useMemo(() => {
    let res = [];
    if (isOriginSelected && selectedOriginMarker) {
      res.push(selectedOriginMarker);
    }
    if (isDestinationSelected && selectedDestinationMarker) {
      res.push(selectedDestinationMarker);
    }
    return res;
  }, [isDestinationSelected, isOriginSelected, selectedDestinationMarker, selectedOriginMarker]);

  const getPreDestinationsSelectorData = useStore(getPreDestinationsSelector);

  const initialMarkers = getPreDestinationsSelectorData
    ? getPreDestinationsSelectorData.map((marker) => createOriginMarker(marker))
    : [];

  const markers = params.showInitialData
    ? isOriginSelected
      ? destinationMarkers
      : initialMarkers
    : destinationMarkers;

  return {
    route,
    markers,
    setSelectedMarker,
    isOriginSelected,
    selectedOriginMarker,
    setSelectedOriginMarker,
    isDestinationSelected,
    selectedDestinationMarker,
    setSelectedDestinationMarker,
    isLoadingDestination,
    currentFares,
    filtersHasChange,
    setIsClearDestination,
    isClearDestination
  };
};

export type OriginMarkersState = ReturnType<typeof useOriginMarkers>;
export type DestinationMarkersState = ReturnType<typeof useDestinationMarkers>;

export default useInteractiveMap;
